import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useFileUpload } from "./useFileUpload";
import { companySchema } from "./companySchema";
import httpClient from "services/network/httpClient";
import {
    CompanyDetailsProps,
    createCompanySettings,
    updateCompanySettings,
} from "./uploadApi";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import GenericModal from "components/Modal";
import ViewCompanyDetails from "./ViewCompanyDetails";
import { showToast } from "components/Toaster";

type CompanyFormValues = {
    companyName: string;
    logo?: FileList;
};

const UploadLogo = () => {
    const { uploadFile } = useFileUpload();
    const [companyDetails, setCompanyDetails] =
        useState<CompanyDetailsProps>();
    const [isOpenToView, setIsOpenToView] = useState(false);

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors, isSubmitting },
    } = useForm<CompanyFormValues>({
        resolver: yupResolver(companySchema, {
            context: {
                hasExistingLogo: !!companyDetails?.logoKey,
            },
        }) as any,
    });

    // ✅ Submit
    const onSubmit = async (data: CompanyFormValues) => {
        try {
            let logoKey = companyDetails?.logoKey;

            // Upload only if new file selected
            if (data.logo && data.logo.length > 0) {
                const file = data.logo[0];
                const { key } = await uploadFile(file);
                logoKey = key;
            }

            const payload = {
                id: companyDetails?.id || 1,
                companyName: data.companyName,
                logoKey,
            };

            if (companyDetails?.id) {
                await updateCompanySettings(payload);
            } else {
                await createCompanySettings(payload);
            }

            // ✅ IMPORTANT FIX: refresh data after save
            await getCompanyDetails();

            // alert("Company settings saved successfully");
            if (!companyDetails?.id) {
                showToast("Company details added successfully", "success");
            }
            showToast("Company details updated successfully", "success");

        } catch (error) {
            console.error(error);
            // alert("Something went wrong");
            showToast("Something went wrong, please try again", "error");
        }
    };

    // ✅ Fetch details
    const getCompanyDetails = async () => {
        try {
            const response: any = await httpClient.get(`/company-settings`);
            const data = response.value;

            setCompanyDetails(data);

            reset({
                companyName: data?.companyName || "",
            });
        } catch (error) {
            console.log("Error message :", error);
        }
    };

    useEffect(() => {
        getCompanyDetails();
    }, []);

    const handleViewClick = () => setIsOpenToView(true);
    const handleViewClose = () => setIsOpenToView(false);

    return (
        <div className="h-full flex items-center justify-center bg-gray-100 px-4 dark:bg-table-bgDark">
            <div className="w-full max-w-md bg-white shadow-lg rounded-2xl p-6 dark:bg-table-headerDark">

                <div className="flex flex-row items-center justify-between mb-6 dark:text-table-textDark">
                    <h2 className="text-2xl font-semibold text-gray-800  dark:text-table-textDark">
                        Company Settings
                    </h2>
                    <button onClick={handleViewClick}>
                        <FontAwesomeIcon icon={faEye} />
                    </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

                    {/* Company Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-table-textDark">
                            Company Name
                        </label>
                        <input
                            {...register("companyName")}
                            className={`w-full px-3 py-2 border dark:bg-table-headerDark dark:text-table-textDark rounded-lg ${errors.companyName
                                ? "border-red-500"
                                : "border-gray-300"
                                }`}
                        />
                        {errors.companyName && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.companyName.message}
                            </p>
                        )}
                    </div>

                    {/* File Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-table-textDark">
                            Upload Logo
                        </label>

                        <input
                            type="file"
                            accept="image/*"
                            {...register("logo")}
                            className={`w-full border rounded-lg ${errors.logo ? "border-red-500 dark:text-table-textDark" : "border-gray-300 dark:text-table-textDark"
                                }`}
                        />

                        {errors.logo && (
                            <p className="text-red-500 text-sm mt-1">
                                {errors.logo.message as string}
                            </p>
                        )}
                    </div>

                    {/* Existing Logo */}
                    {companyDetails?.logoUrl && (
                        <div>
                            <p className="text-sm text-gray-600 mb-1 dark:text-table-textDark">
                                Current Logo:
                            </p>
                            <img
                                src={companyDetails.logoUrl}
                                alt="Company Logo"
                                className="h-16 w-16 object-cover rounded border"
                            />
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-2.5 rounded-lg bg-blue-600 text-white"
                    >
                        {isSubmitting ? "Submitting..." : "Save Settings"}
                    </button>
                </form>
            </div>

            {/* Modal */}
            {isOpenToView && companyDetails && (
                <GenericModal
                    isOpen={isOpenToView}
                    onClose={handleViewClose}
                    header="Company Details"
                    customWidth="md:w-96"
                >
                    <ViewCompanyDetails comapanyData={companyDetails} />
                </GenericModal>
            )}
        </div>
    );
};

export default UploadLogo;